import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, DatePicker, Table } from 'antd';
import {
  ColumnsType,
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import { RangeValue } from 'rc-picker/lib/interface';
import moment, { Moment } from 'moment';
import pluralize from 'pluralize';
import queryString from 'query-string';
import cx from 'classnames';
import { useDidUpdate } from 'rooks';
import { isNil } from 'lodash';

import { DateParams } from 'utils/filterUtils';
import { SortingDirectionEnum } from 'enums';
import useSortColumns, { ISortParams } from 'hooks/useSortColumns';
import { pageDefaultSizeTable } from 'constants/index';
import {
  getWeeklyTrendMixReport,
  getWeeklyTrendMixReportExcel,
} from 'redux/reducers/homeDashboardSlice';
import { getSortOrderRequestValue } from 'utils/sortUtils';
import {
  getDateWithoutTime,
  getDateRangeTillLastSunday,
  disableSpecificNumberDates,
} from 'utils/dateUtils';
import {
  getDynamicColumnsReportFormattedValues,
  mapDatesToColumnFormat,
} from 'utils/dynamicColumnsUtils';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import {
  TrendMixReportItemViewModel,
  TrendMixReportViewModel,
} from 'api/backofficeApiClient';
import { CustomerBillingProfitSortFieldEnum } from 'features/home/UserHomePage/enums';
import LastSyncDateSection from 'components/LastSyncDateSection';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import { useAppDispatch } from 'hooks/useAppDispatch';

import commonStyles from '../UserHomePage.module.less';
import styles from './WeeklyTrendMixReport.module.less';

enum WeeklyTrendMixReportFilterTypes {
  Date = 'date',
}

type WeeklyReportFilters = {
  [WeeklyTrendMixReportFilterTypes.Date]: DateParams | undefined;
};

const defaultSortParams: ISortParams<CustomerBillingProfitSortFieldEnum> = {
  sortOrder: 'ascend',
  sortBy: CustomerBillingProfitSortFieldEnum.CustomerName,
};

const dateRangeDurationInWeeks = 51;
const defaultDateRangeDurationInWeeks = 7;

export default function WeeklyTrendMixReport() {
  const [weeklyReportData, setWeeklyReportData] =
    useState<TrendMixReportViewModel>();
  const [isWeeklyReportDataLoading, setIsWeeklyReportDataLoading] =
    useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { sortBy, sortOrder, startDate, endDate } = queryString.parse(
    location.search,
  );
  const defaultDateFilter: DateParams = getDateRangeTillLastSunday(
    defaultDateRangeDurationInWeeks,
  );
  const initialFilters = {
    [WeeklyTrendMixReportFilterTypes.Date]:
      startDate && endDate
        ? {
            startDate: moment(startDate, 'MM/DD/YYYY'),
            endDate: moment(endDate, 'MM/DD/YYYY'),
          }
        : defaultDateFilter,
  };
  const [selectedDate, setSelectedDate] = useState<DateParams | undefined>(
    undefined,
  );
  const [filters, setFilters] = useState<WeeklyReportFilters>(initialFilters);
  const initialSortParams: ISortParams<CustomerBillingProfitSortFieldEnum> =
    sortBy && sortOrder
      ? {
          sortBy: sortBy,
          sortOrder,
        }
      : defaultSortParams;
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const dispatch = useAppDispatch();

  const columns = useMemo(() => {
    const columns: ColumnsType<TrendMixReportItemViewModel> = [
      {
        title: 'Customer',
        key: CustomerBillingProfitSortFieldEnum.CustomerName,
        fixed: 'left',
        render: (record: TrendMixReportItemViewModel) => (
          <Link className="wrapped-text" to={`/customer/${record.customerKey}`}>
            {record.customerName}
          </Link>
        ),
      },
      {
        title: 'Total',
        key: CustomerBillingProfitSortFieldEnum.Total,
        render: (record: TrendMixReportItemViewModel) => record.billAmountTotal,
      },
      {
        title: '% of Total',
        key: CustomerBillingProfitSortFieldEnum.TotalPercentage,
        render: (record: TrendMixReportItemViewModel) =>
          record.percentageOfGreatTotal,
      },
    ];

    const sortedDates = mapDatesToColumnFormat(
      weeklyReportData?.grandTotal?.sundayReportDetails || {},
    );

    const columnsDate: ColumnsType<TrendMixReportItemViewModel> =
      sortedDates.map((dateDetails) => {
        return {
          key: dateDetails.date,
          title: dateDetails.date,
          render: (record) => record[dateDetails.date],
        };
      });
    // Insert dynamic Sundays after the first column.
    columnsDate.length && columns.splice(1, 0, ...columnsDate);

    return columns;
  }, [weeklyReportData]);

  const { sortColumns, sortParams, setSortParams } = useSortColumns(
    columns,
    initialSortParams,
  );

  const weeklyReportCountMessage = pluralize(
    'customer',
    weeklyReportData?.totalCount || 0,
    true,
  );

  useDidUpdate(() => {
    const queryData = {
      sortBy: sortParams.sortBy,
      sortOrder: sortParams.sortOrder,
      startDate: filters[WeeklyTrendMixReportFilterTypes.Date]?.startDate
        ? filters[WeeklyTrendMixReportFilterTypes.Date]?.startDate?.format(
            'MM/DD/YYYY',
          )
        : undefined,
      endDate: filters[WeeklyTrendMixReportFilterTypes.Date]?.endDate
        ? filters[WeeklyTrendMixReportFilterTypes.Date]?.endDate?.format(
            'MM/DD/YYYY',
          )
        : undefined,
    };

    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
    // eslint-disable-next-line
  }, [sortParams.sortBy, sortParams.sortOrder, filters]);

  const loadWeeklyReportData = useCallback(async () => {
    setIsWeeklyReportDataLoading(true);

    const result = await dispatch(
      getWeeklyTrendMixReport({
        pageNumber: currentTablePage - 1,
        sortField: sortParams.sortBy,
        sortType: getSortOrderRequestValue(sortParams.sortOrder),
        startDate: filters[WeeklyTrendMixReportFilterTypes.Date]
          ? getDateWithoutTime(
              filters[WeeklyTrendMixReportFilterTypes.Date]?.startDate,
            )
          : undefined,
        endDate: filters[WeeklyTrendMixReportFilterTypes.Date]
          ? getDateWithoutTime(
              filters[WeeklyTrendMixReportFilterTypes.Date]?.endDate,
            )
          : undefined,
        pageSize: pageDefaultSizeTable,
      }),
    );
    if (getWeeklyTrendMixReport.fulfilled.match(result)) {
      setWeeklyReportData(
        getDynamicColumnsReportFormattedValues(result.payload),
      );
    } else {
      setWeeklyReportData({
        grandTotal: undefined,
        totalCount: 0,
        items: [],
      });
    }

    setIsWeeklyReportDataLoading(false);
  }, [
    dispatch,
    currentTablePage,
    sortParams.sortOrder,
    sortParams.sortBy,
    filters,
  ]);

  const loadInitialData = useCallback(async () => {
    await loadWeeklyReportData();

    setIsInitialDataLoaded(true);
  }, [loadWeeklyReportData]);

  const handleDownloadReport = async () => {
    const result = await dispatch(
      getWeeklyTrendMixReportExcel({
        pageNumber: currentTablePage - 1,
        sortField: sortParams.sortBy,
        sortType: getSortOrderRequestValue(sortParams.sortOrder),
        startDate: filters[WeeklyTrendMixReportFilterTypes.Date]
          ? getDateWithoutTime(
              filters[WeeklyTrendMixReportFilterTypes.Date]?.startDate,
            )
          : undefined,
        endDate: filters[WeeklyTrendMixReportFilterTypes.Date]
          ? getDateWithoutTime(
              filters[WeeklyTrendMixReportFilterTypes.Date]?.endDate,
            )
          : undefined,
        pageSize: pageDefaultSizeTable,
      }),
    );

    if (getWeeklyTrendMixReportExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleCalendarChange = (dates: RangeValue<Moment>) => {
    if (dates) {
      setSelectedDate({
        startDate: dates[0],
        endDate: dates[1],
      });
    } else {
      setSelectedDate(undefined);
    }
  };

  const handleSelectDate =
    (type: WeeklyTrendMixReportFilterTypes) => (date: RangeValue<Moment>) => {
      if (date) {
        // Reset sort params by date column if
        // the date is not in the date picker's range
        if (
          !isNil(sortParams.sortBy) &&
          moment(sortParams.sortBy).isValid() &&
          !moment(sortParams.sortBy).isBetween(date[0], date[1], 'D', '[]')
        ) {
          setSortParams(defaultSortParams);
        }

        setFilters({
          ...filters,
          [type]: {
            startDate: date[0],
            endDate: date[1],
          },
        });
      } else {
        setFilters({
          ...filters,
          [type]: undefined,
        });
      }
      setCurrentTablePage(1);
    };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filter: Record<string, FilterValue | null>,
    sort:
      | SorterResult<TrendMixReportItemViewModel>
      | SorterResult<TrendMixReportItemViewModel>[],
  ) => {
    const sortOrder =
      (sort as SorterResult<TrendMixReportItemViewModel>).order ||
      SortingDirectionEnum.Ascend;
    const sortBy = (sort as SorterResult<TrendMixReportItemViewModel>)
      .columnKey as CustomerBillingProfitSortFieldEnum;

    setSortParams(({ sortBy: prevSortBy, sortOrder: prevSortOrder }) => {
      if (prevSortBy !== sortBy || prevSortOrder !== sortOrder) {
        // Reset table page if sorting has changed
        setCurrentTablePage(1);
      } else {
        setCurrentTablePage(pagination.current || 1);
      }

      return {
        sortOrder,
        sortBy,
      };
    });
  };

  const rangePickerValue = useMemo((): RangeValue<Moment> | undefined => {
    if (selectedDate) {
      return Object.entries(selectedDate).length
        ? [
            selectedDate.startDate
              ? moment(selectedDate.startDate, 'MM/DD/YYYY')
              : null,
            selectedDate.endDate
              ? moment(selectedDate.endDate, 'MM/DD/YYYY')
              : null,
          ]
        : undefined;
    }

    if (filters[WeeklyTrendMixReportFilterTypes.Date]) {
      return [
        moment(
          filters[WeeklyTrendMixReportFilterTypes.Date]?.startDate,
          'MM/DD/YYYY',
        ),
        moment(
          filters[WeeklyTrendMixReportFilterTypes.Date]?.endDate,
          'MM/DD/YYYY',
        ),
      ];
    }
  }, [selectedDate, filters]);

  const onOpenChange = (open: boolean) => {
    if (open) {
      setSelectedDate({});
    } else {
      setSelectedDate(undefined);
    }
  };

  return (
    <div className="page-wrapper scroll-box">
      <div className="page-holder">
        <BasePageLabel label="Dashboard details">
          {weeklyReportData?.lastSyncDate && (
            <div className="title-line__info">
              <LastSyncDateSection
                syncDate={weeklyReportData.lastSyncDate}
                title="Customer trends & mix report is automatically refreshed every 15 minutes."
              />
            </div>
          )}
        </BasePageLabel>
        <div className="page-box page-inner">
          <div className="page-inner-container">
            <BaseTableHeader
              title="Trend - Weekly Billing by Customer"
              enableSubTitle={isInitialDataLoaded}
              subTitle={weeklyReportCountMessage}
            >
              <div>
                <DatePicker.RangePicker
                  key="filterByDate"
                  format="MM/DD/YYYY"
                  separator="-"
                  className="d-flex"
                  allowClear={false}
                  onCalendarChange={handleCalendarChange as any}
                  value={rangePickerValue}
                  disabledDate={
                    disableSpecificNumberDates(
                      selectedDate?.startDate,
                      selectedDate?.endDate,
                      dateRangeDurationInWeeks,
                    ) as any
                  }
                  onOpenChange={onOpenChange}
                  onChange={
                    handleSelectDate(
                      WeeklyTrendMixReportFilterTypes.Date,
                    ) as any
                  }
                />
              </div>
              <div>
                <Button
                  className={commonStyles.saveBtn}
                  onClick={handleDownloadReport}
                  icon={<span className="icon icon-xls-file" />}
                />
              </div>
            </BaseTableHeader>
            <Table
              className={cx(styles.table, 'custom-table')}
              pagination={{
                total: weeklyReportData?.totalCount,
                pageSize: pageDefaultSizeTable,
                current: currentTablePage,
                showSizeChanger: false,
              }}
              showSorterTooltip={false}
              summary={() => (
                <Table.Summary fixed={true}>
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={1} index={0}>
                      Grand Total
                    </Table.Summary.Cell>
                    {weeklyReportData?.grandTotal?.sundayReportDetails &&
                      mapDatesToColumnFormat(
                        weeklyReportData?.grandTotal?.sundayReportDetails,
                      ).map((dateDetails, index: number) => (
                        <Table.Summary.Cell
                          colSpan={1}
                          index={2 + index++}
                          key={dateDetails.date}
                        >
                          {dateDetails.billing}
                        </Table.Summary.Cell>
                      ))}
                    <Table.Summary.Cell colSpan={1} index={1}>
                      {weeklyReportData?.grandTotal?.billAmountTotal}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell colSpan={1} index={2}>
                      {weeklyReportData?.grandTotal?.percentageOfGreatTotal}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
              onChange={handleTableChange}
              columns={sortColumns}
              dataSource={weeklyReportData?.items}
              rowKey={(record) => record?.customerKey as string}
              loading={isWeeklyReportDataLoading}
              scroll={{ x: '100%' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
